import { DRAFT_SECTION_SENTINEL } from './draft-sentinel';

/** Legacy unnumbered persisted title for a section whose heading has no visible text. */
export const EMPTY_SECTION_HEADING_MARKER = '<!-- section-heading-empty -->';

/** Creates the per-document identity marker emitted by current persistence writers. */
export const createEmptySectionHeadingMarker = (identity: number): string =>
  `<!-- section-heading-empty:${identity} -->`;

/**
 * Matches the reserved empty-heading comment, tolerating inner whitespace and
 * an optional `:N` identity suffix (`<!-- section-heading-empty:2 -->`).
 * Readers keep accepting the unnumbered form for lazy migration of existing
 * notes and templates (older clients also keep writing it during rollout).
 */
export const EMPTY_SECTION_HEADING_MARKER_REGEX =
  /^<!--\s*section-heading-empty(?::\d+)?\s*-->$/;

/**
 * A fresh regex matching one top-level `<section …>…</section>` element,
 * tolerating `>` inside quoted attribute values (group 1 = attributes,
 * group 2 = inner content). Returned per-call so the shared `/g` state is never
 * aliased across the two consumers (`stripEmptySectionHeadingPresentationHtml`
 * here and `stripSectionWrappers` in `sectionParser`), keeping one grammar.
 */
export const createSectionElementRegex = (): RegExp =>
  /<section\b((?:[^>"']|"[^"]*"|'[^']*')*?)>([\s\S]*?)<\/section>/gi;

/**
 * A logical empty H1 in stored markdown. `#` and `# ` are accepted as legacy
 * inputs; numbered comment markers are the stable output because trailing
 * whitespace is routinely trimmed by storage and transport layers.
 */
const EMPTY_SECTION_HEADING_LINE_REGEX =
  /^ {0,3}#(?: *| +<!--\s*section-heading-empty(?::\d+)?\s*--> *)$/;

/** True when a value is exactly the reserved empty-heading comment. */
export const isEmptySectionHeadingMarker = (raw: string | undefined): boolean =>
  raw !== undefined && EMPTY_SECTION_HEADING_MARKER_REGEX.test(raw.trim());

/** True for a complete legacy or numbered empty section-heading line. */
export const isEmptySectionHeadingLine = (line: string | undefined): boolean =>
  line !== undefined && EMPTY_SECTION_HEADING_LINE_REGEX.test(line.replace(/\r$/, ''));

/**
 * Replaces logical empty heading lines with a parse-safe heading payload.
 * Callers protect fenced code blocks before applying the section-line grammar.
 */
export const replaceEmptySectionHeadingLines = (
  markdown: string,
  headingContent: string
): string => {
  const lines = markdown.split('\n');

  return lines
    .map((line) =>
      isEmptySectionHeadingLine(line)
        ? `# ${headingContent}${line.endsWith('\r') ? '\r' : ''}`
        : line
    )
    .join('\n');
};

const stripMatchingHeadingLines = (
  markdown: string,
  isTargetLine: (line: string | undefined) => boolean
): string => {
  const lines = markdown.split('\n');
  const result: string[] = [];

  for (let index = 0; index < lines.length; index++) {
    if (!isTargetLine(lines[index])) {
      result.push(lines[index] ?? '');
      continue;
    }

    if ((lines[index + 1] ?? '').replace(/\r$/, '') === '') {
      index++;
    }
  }

  return result.join('\n');
};

/**
 * Removes empty section-heading lines (numbered or legacy marker and legacy
 * `#`/`# ` forms) from markdown destined for copy, print, export, or another
 * presentation surface. A single blank separator directly after the heading is
 * consumed with it so the body does not gain a leading blank line.
 */
export const stripEmptySectionHeadingLines = (markdown: string): string =>
  stripMatchingHeadingLines(markdown, isEmptySectionHeadingLine);

/** A complete line that is only a numbered or legacy marker heading. */
const EMPTY_SECTION_HEADING_MARKER_LINE_REGEX =
  /^ {0,3}# +<!--\s*section-heading-empty(?::\d+)?\s*--> *$/;

/** True for a marker heading line; legacy `#`/`# ` lines don't match. */
export const isEmptySectionHeadingMarkerLine = (line: string | undefined): boolean =>
  line !== undefined &&
  EMPTY_SECTION_HEADING_MARKER_LINE_REGEX.test(line.replace(/\r$/, ''));

/**
 * Like {@link stripEmptySectionHeadingLines}, but removes only marker forms.
 * For conversion paths that also hydrate editable non-section
 * content (context notes, dictation, chat inserts): the marker can only
 * originate from section persistence and must never render, but a literal
 * `#` line there is the user's own content and must survive the round trip.
 */
export const stripEmptySectionHeadingMarkerLines = (markdown: string): string =>
  stripMatchingHeadingLines(markdown, isEmptySectionHeadingMarkerLine);

/** A complete heading line containing only the editor's draft sentinel. */
const DRAFT_SECTION_SENTINEL_LINE_REGEX = new RegExp(
  `^ {0,3}# +${DRAFT_SECTION_SENTINEL} *$`
);

const isPersistedEmptySectionHeadingLine = (line: string | undefined): boolean =>
  line !== undefined &&
  (isEmptySectionHeadingMarkerLine(line) ||
    DRAFT_SECTION_SENTINEL_LINE_REGEX.test(line.replace(/\r$/, '')));

/**
 * Removes only forms that can be emitted by empty-section persistence: the
 * numbered or legacy marker and the historical editor sentinel. This is safe for
 * legacy copy/export fallbacks that may also receive non-section content:
 * literal `#` / `# ` lines and zero-width spaces in body text are preserved.
 */
export const stripPersistedEmptySectionHeadingLines = (markdown: string): string =>
  stripMatchingHeadingLines(markdown, isPersistedEmptySectionHeadingLine)
    .split('\n')
    .map((line) =>
      /^ {0,3}# +/.test(line.replace(/\r$/, ''))
        ? line.replaceAll(DRAFT_SECTION_SENTINEL, '')
        : line
    )
    .join('\n');

/**
 * True when editor heading HTML has no visible title after removing the
 * zero-width editor sentinel, or contains only the persisted marker.
 */
export const isEmptySectionHeadingContent = (content: string): boolean => {
  if (isEmptySectionHeadingMarker(content)) return true;
  return !content
    .replaceAll(DRAFT_SECTION_SENTINEL, '')
    .replace(/<[^>]*>/g, '')
    .replace(/&nbsp;/g, ' ')
    .trim();
};

/** Remove the editor-only sentinel while preserving real heading content. */
export const stripDraftSectionSentinel = (content: string): string =>
  content.replaceAll(DRAFT_SECTION_SENTINEL, '');

/**
 * Removes empty heading DOM and editor sentinels from presentation HTML. This
 * is intentionally not used for editor hydration: the live editor needs the
 * sentinel in order to render its "New Section" placeholder.
 *
 * Sentinel removal is scoped to headings — the only place the editor ever
 * inserts it. A blanket document-wide sweep would silently delete a zero-width
 * space that appears legitimately in pasted clinical body text on copy / print
 * / export, so we never reach past the `<h1>` elements.
 */
export const stripEmptySectionHeadingPresentationHtml = (html: string): string =>
  html
    .replace(
      createSectionElementRegex(),
      (section, _attrs: string, sectionContent: string) => {
        const heading = /^\s*<h1\b[^>]*>([\s\S]*?)<\/h1>/i.exec(sectionContent);
        return heading && isEmptySectionHeadingContent(heading[1] ?? '')
          ? sectionContent.slice(heading[0].length)
          : section;
      }
    )
    .replace(/<h1\b[^>]*>([\s\S]*?)<\/h1>/gi, (match, content: string) =>
      isEmptySectionHeadingContent(content) ? '' : stripDraftSectionSentinel(match)
    );

/**
 * Section-close delimiter.
 *
 * A section in stored note markdown is delimited only by its opening `# `
 * heading and the next `# ` heading (or EOF), so a top-level block that follows
 * a section — text the user typed *outside* the section — is indistinguishable
 * from the section's body and gets re-absorbed on reload. This marker closes a
 * section explicitly so the following block stays top-level across a
 * serialize→store→parse round trip.
 *
 * It is an HTML comment: invisible in every real markdown renderer and consumed
 * by both editors' parsers (never rendered as an editor node). It is emitted
 * **conditionally** — only when a section is immediately followed by a
 * non-section block — so fully-sectioned notes are byte-identical to before and
 * never gain a marker.
 *
 * Shared by both editor twins (markdown-native `section-markdown.ts`, legacy
 * vMarkdown `sectionParser.ts` / `htmlToMarkdown.ts`) so a note authored in one
 * round-trips in the other, and by the raw-string EHR push path which strips it.
 */
export const SECTION_END_MARKER = '<!-- section-end -->';

/** Matches the marker as a standalone line (tolerating inner whitespace). */
export const SECTION_END_MARKER_REGEX = /^<!--\s*section-end\s*-->$/;

/** True when a marked token is the section-close marker. */
export const isSectionEndHtml = (raw: string | undefined): boolean =>
  raw !== undefined && SECTION_END_MARKER_REGEX.test(raw.trim());

/** Emitted outbound by `stripSectionWrappers`; the `<p>` wrapper serializes to a standalone marker line. */
export const SECTION_END_MARKER_HTML = `<p>${SECTION_END_MARKER}</p>`;

/**
 * True when a top-level HTML element (in the legacy vMarkdown pipeline) is only
 * the section-close marker — bare, or wrapped in a `<p>` by the paragraph pass.
 * Unwraps the optional `<p>` and delegates to {@link isSectionEndHtml}, so both
 * editor twins share a single definition of the marker; there is no second
 * regex to drift out of sync (and nothing extra to unpick when vMarkdown is
 * retired in favour of markdown-native).
 */
export const isSectionEndElement = (element: string): boolean =>
  isSectionEndHtml(
    element
      .trim()
      .replace(/^<p>/i, '')
      .replace(/<\/p>$/i, '')
  );

/**
 * Removes section persistence markers from raw stored note markdown. For the
 * few consumers that read `consult_note.result` / `edited_note` verbatim rather
 * than round-tripping through the editor (which strips the markers on parse) —
 * e.g. the EHR push path — so neither marker reaches an external system.
 *
 * Three persistence forms are stripped: the section-close marker, numbered or
 * legacy empty-heading marker lines (`# <!-- section-heading-empty:N -->`),
 * and the historical draft-sentinel heading. A draft sentinel attached to a
 * real title is removed from the title while its visible text survives. Bare
 * `#` / `# ` lines remain untouched because they may be authored content.
 *
 * Neither strip protects fenced code blocks, matching how the section-close
 * marker has always been removed: both are reserved vocabulary that can only
 * originate from section persistence, so a fence is not a reason to let one
 * through to an external system.
 *
 * A no-op when no marker is present. Each section-close marker is removed
 * together with its own surrounding newlines only (a paragraph-separated marker
 * collapses to one blank line, a single-newline marker to one newline) — the
 * normalization is scoped to each removal site, so newline runs elsewhere in
 * the document (e.g. inside fenced code blocks) are never touched.
 */
export const stripSectionMarkers = (markdown: string): string =>
  stripPersistedEmptySectionHeadingLines(
    markdown
      // Paragraph-separated marker (markdown-native form): collapse to one blank line.
      .replace(/\n\n[^\S\n]*<!--\s*section-end\s*-->[^\S\n]*\n\n/g, '\n\n')
      // Single-newline marker (vMarkdown form): collapse to one newline.
      .replace(/\n[^\S\n]*<!--\s*section-end\s*-->[^\S\n]*\n/g, '\n')
      // Any leftover marker (doc start/end, or inline): remove without touching
      // newlines. Starts at the literal `<!--` rather than a leading whitespace
      // run — unanchored, that run is re-tried and backtracked at every offset,
      // which is quadratic on long space/tab runs. Indentation before a
      // standalone marker is already consumed by the two anchored passes above.
      .replace(/<!--\s*section-end\s*-->[^\S\n]*/g, '')
  );
